import { useState, useEffect } from 'react'
import { contentService } from '../utils/contentService'
import { storeInfoService, StoreInfo } from '../services/storeInfoService'
import SEO from '../components/SEO'

function BootstrapContact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: 'general'
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [contactContent, setContactContent] = useState(contentService.getContactContent())
  const [storeInfo, setStoreInfo] = useState<StoreInfo>(storeInfoService.getStoreInfo())

  useEffect(() => {
    // Listen for content updates
    const unsubscribeContent = contentService.subscribe(() => {
      console.log('BootstrapContact: Content updated, reloading...')
      setContactContent(contentService.getContactContent())
    })

    // Listen for store info updates
    const unsubscribeStore = storeInfoService.subscribe(() => {
      setStoreInfo(storeInfoService.getStoreInfo())
    })
    
    return () => {
      unsubscribeContent()
      unsubscribeStore()
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Create form data for Netlify Function
      const formDataObj = new FormData(e.target as HTMLFormElement)

      const response = await fetch('/.netlify/functions/contact-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj as any).toString()
      })

      if (response.ok) {
        alert('Thank you for your message! We\'ll get back to you within 24 hours.')

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          inquiryType: 'general'
        })
      } else {
        const errorData = await response.json().catch(() => ({}))
        console.error('Form submission error:', errorData)
        throw new Error(errorData.message || 'Form submission failed')
      }
    } catch (error) {
      console.error('Contact form error:', error)
      alert(`There was an error sending your message. Please try again or call us directly at ${storeInfo.contact.phone}.`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="py-5">
      <SEO
        title="Contact Us - Get in Touch with The Salty Mermaid"
        description="Contact The Salty Mermaid Art Gallery & Studio in Ormond Beach, FL. Call (904) 704-7449 or visit us at 1546 Ocean Shore Blvd. We're here to answer your questions about classes, gallery, and supplies."
        canonicalUrl="https://saltymermaidart.com/contact"
      />
      <div className="container">

        {/* Header */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-8">
            <div className="card shadow-lg border-0" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4 text-center">
                <h1 className="display-4 fw-bold mb-4">Contact Us</h1>
                <p className="h5 text-dark mb-0" style={{lineHeight: '1.6'}}>
                  We'd love to hear from you! Get in touch with any questions about our classes, gallery, or art supplies.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-5">
          
          {/* Contact Form */}
          <div className="col-lg-8">
            <div className="card shadow-sm border-0">
              <div className="card-body p-4">
                <h3 className="card-title text-primary fw-bold mb-4">Send us a Message</h3>
                
                <form 
                  onSubmit={handleSubmit}
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                >
                  {/* Netlify form detection */}
                  <input type="hidden" name="form-name" value="contact" />
                  
                  {/* Honeypot field for spam protection */}
                  <div style={{ display: 'none' }}>
                    <label>
                      Don't fill this out if you're human: <input name="bot-field" />
                    </label>
                  </div>
                  <div className="row g-3">
                    
                    {/* Name and Email */}
                    <div className="col-md-6">
                      <label htmlFor="name" className="form-label fw-semibold">Full Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Your full name"
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="email" className="form-label fw-semibold">Email Address *</label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your.email@example.com"
                      />
                    </div>

                    {/* Phone and Inquiry Type */}
                    <div className="col-md-6">
                      <label htmlFor="phone" className="form-label fw-semibold">Phone Number</label>
                      <input
                        type="tel"
                        className="form-control"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(386) 555-0123"
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="inquiryType" className="form-label fw-semibold">Inquiry Type</label>
                      <select
                        className="form-select"
                        id="inquiryType"
                        name="inquiryType"
                        value={formData.inquiryType}
                        onChange={handleInputChange}
                      >
                        <option value="general">General Question</option>
                        <option value="classes">Art Classes</option>
                        <option value="gallery">Gallery Inquiry</option>
                        <option value="supplies">Art Supplies</option>
                        <option value="events">Private Events</option>
                        <option value="artist">Artist Submission</option>
                      </select>
                    </div>

                    {/* Subject */}
                    <div className="col-12">
                      <label htmlFor="subject" className="form-label fw-semibold">Subject *</label>
                      <input
                        type="text"
                        className="form-control"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        placeholder="Brief subject line"
                      />
                    </div>

                    {/* Message */}
                    <div className="col-12">
                      <label htmlFor="message" className="form-label fw-semibold">Message *</label>
                      <textarea
                        className="form-control"
                        id="message"
                        name="message"
                        rows={6}
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        placeholder="Tell us how we can help you..."
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <div className="col-12">
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending...
                          </>
                        ) : (
                          'Send Message'
                        )}
                      </button>
                    </div>

                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="col-lg-4">
            
            {/* Location & Hours */}
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <h4 className="card-title text-primary fw-bold mb-4">📍 Visit Our Studio</h4>
                
                <div className="mb-4">
                  <h6 className="fw-bold text-dark mb-2">Address</h6>
                  <p className="text-muted mb-0">
                    {storeInfo.address.street}<br />
                    {storeInfo.address.city}, {storeInfo.address.state} {storeInfo.address.zipCode}<br />
                    {storeInfo.address.description && <small className="text-success"><em>{storeInfo.address.description}</em></small>}
                  </p>
                </div>

                <div className="mb-4">
                  <h6 className="fw-bold text-dark mb-2">Studio Hours</h6>
                  <div className="small text-muted">
                    {storeInfoService.getWorkingDaysHours().map((period, index) => (
                      <p key={index} className={index === storeInfoService.getWorkingDaysHours().length - 1 ? "mb-0" : "mb-1"}>
                        <strong>{period.days}:</strong> {period.hours}
                      </p>
                    ))}
                  </div>
                </div>

                <div>
                  <h6 className="fw-bold text-dark mb-2">Contact Info</h6>
                  <p className="text-muted mb-2">
                    📞 <strong>Phone:</strong><br />
                    <a href={`tel:${storeInfo.contact.phone.replace(/[^\d]/g, '')}`} className="text-decoration-none">{storeInfo.contact.phone}</a>
                  </p>
                  <p className="text-muted mb-2">
                    ✉️ <strong>Email:</strong><br />
                    <a href={`mailto:${storeInfo.contact.email}`} className="text-decoration-none">{storeInfo.contact.email}</a>
                  </p>
                  <p className="text-muted mb-0">
                    🌐 <strong>Website:</strong><br />
                    <a href={`https://${storeInfo.contact.website}`} className="text-decoration-none" target="_blank" rel="noopener noreferrer">{storeInfo.contact.website}</a>
                  </p>
                </div>
              </div>
            </div>

            {/* Social Media & Additional Info */}
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h4 className="card-title text-primary fw-bold mb-4">🌐 Connect With Us</h4>
                
                <div className="mb-4">
                  <p className="text-muted mb-3">Follow us on social media for the latest updates, student artwork, and class announcements!</p>
                  
                  <div className="d-grid gap-2">
                    {storeInfo.social.facebook && (
                      <a 
                        href={storeInfo.social.facebook} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary btn-sm"
                      >
                        📘 Follow us on Facebook
                      </a>
                    )}
                  </div>
                </div>

                <div>
                  <h6 className="fw-bold text-dark mb-2">Quick Facts</h6>
                  <ul className="list-unstyled text-muted small">
                    <li className="mb-1">🎨 75+ local artists featured</li>
                    <li className="mb-1">📚 15+ different class types</li>
                    <li className="mb-1">🏖️ Beachside inspiration daily</li>
                    <li className="mb-1">🛍️ Art supplies available on-site</li>
                    <li>🎯 All skill levels welcome</li>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* FAQ Section */}
        <div className="row mt-5 pt-5 border-top">
          <div className="col-12">
            <div className="card shadow-lg border-0 mb-5" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}>
              <div className="card-body p-4 text-center">
                <h3 className="text-primary fw-bold mb-0">Frequently Asked Questions</h3>
              </div>
            </div>
            
            <div className="row g-4">
              <div className="col-md-6">
                <div className="card shadow-sm border-0 h-100" style={{
                  borderRadius: '15px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                }}>
                  <div className="card-body p-4">
                    <h5 className="fw-bold text-dark mb-3">Do I need to bring my own art supplies?</h5>
                    <p className="text-dark mb-0">
                      Most of our classes include all necessary materials, but we also have a well-stocked supply shop 
                      if you prefer to bring your own or need additional materials.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="card shadow-sm border-0 h-100" style={{
                  borderRadius: '15px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                }}>
                  <div className="card-body p-4">
                    <h5 className="fw-bold text-dark mb-3">What if I've never done art before?</h5>
                    <p className="text-dark mb-0">
                      Perfect! Many of our students are complete beginners. Our instructors are experienced in working 
                      with all skill levels and will guide you step-by-step.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="card shadow-sm border-0 h-100" style={{
                  borderRadius: '15px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                }}>
                  <div className="card-body p-4">
                    <h5 className="fw-bold text-dark mb-3">Can I schedule a private class or event?</h5>
                    <p className="text-dark mb-0">
                      Absolutely! We offer private classes, birthday parties, team building events, and other custom 
                      experiences. Contact us to discuss your needs.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="card shadow-sm border-0 h-100" style={{
                  borderRadius: '15px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                }}>
                  <div className="card-body p-4">
                    <h5 className="fw-bold text-dark mb-3">How do I submit artwork for the gallery?</h5>
                    <p className="text-dark mb-0">
                      We're always looking for new local talent! Please bring samples of your work to the studio or 
                      email us photos with your artist statement and contact information.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default BootstrapContact